import type { Dispatch, SetStateAction } from "react";
import type { UseFormReturn } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useSearch } from "@tanstack/react-router";
import { convert } from "html-to-text";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import * as z from "zod";

import { tooLongTextRefine } from "@/features/request/create/shared/characterLimit";
import { useIsRequestFormDirty } from "@/hooks/useIsRequestFormDirty";
import { useUser } from "@/hooks/useUser";

import { fileAssetSchema, linkAssetSchema } from "../../shared/utils";

const REQUEST_DESCRIPTION_FIELD = {
  label: "Request description",
  maxLength: 64_000,
  path: "requestDescriptionHTML",
} as const;

export const CHARACTER_LIMIT_FIELDS = [REQUEST_DESCRIPTION_FIELD] as const;

const createWebsiteImplementationRequestFormSchema = (userRole: string) =>
  z.object({
    /* eslint-disable perfectionist/sort-objects */
    requestName: z
      .string()
      .min(1, { message: "Request name is required" })
      .max(255, { message: "Request name is too long" }),
    designTypeId: z
      .number({
        required_error: "Design type is required",
      })
      .nullable()
      .refine((val): val is number => val !== null, {
        message: "Design type is required",
      }),
    brandId: z.coerce.number().optional().nullable(),
    requestDescriptionHTML: z
      .string()
      .refine((value) => convert(value).trim().length > 0, {
        message: "Request description is required",
      })
      .superRefine(
        tooLongTextRefine(
          REQUEST_DESCRIPTION_FIELD.label,
          undefined,
          REQUEST_DESCRIPTION_FIELD.maxLength,
        ),
      ),
    requestDescription: z.string(),
    // Assets and inspirations
    assets: z.array(z.union([fileAssetSchema, linkAssetSchema])),
    inspirations: z.array(z.union([fileAssetSchema, linkAssetSchema])),
    // Technical details
    preferredDesignerIds: z.array(z.number()),
    designComplexityId: [
      "customer-success",
      "super-admin",
      "team-leader",
    ].includes(userRole)
      ? z.number({ required_error: "Design complexity is required" })
      : z.any().optional(),
    collaboratorIds: z.array(z.number()),
    /* eslint-enable perfectionist/sort-objects */
  });

export type CreateWebsiteImplementationRequestFormData = z.infer<
  ReturnType<typeof createWebsiteImplementationRequestFormSchema>
>;

export function useCreateWebsiteImplementationRequestForm(): {
  setShowWarningModal: Dispatch<SetStateAction<boolean>>;
  showWarningModal: boolean;
} & UseFormReturn<CreateWebsiteImplementationRequestFormData> {
  const { data: user } = useUser();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const setIsDirty = useIsRequestFormDirty((state) => state.setIsDirty);
  const { designType } = useSearch({
    from: "/_auth/_app-shell/requests/create",
  });

  const form = useForm<CreateWebsiteImplementationRequestFormData>({
    defaultValues: {
      /* eslint-disable perfectionist/sort-objects */
      requestName: "",
      designTypeId: designType,
      brandId: undefined,
      requestDescriptionHTML: "",
      requestDescription: "",
      assets: [],
      inspirations: [],
      preferredDesignerIds: [],
      designComplexityId: undefined,
      collaboratorIds: [],
      /* eslint-enable perfectionist/sort-objects */
    },
    resolver: zodResolver(
      createWebsiteImplementationRequestFormSchema(user?.user.roleName || ""),
    ),
  });

  const { brand_id } = useSearch({
    from: "/_auth/_app-shell/requests/create",
  });

  useEffect(() => {
    if (brand_id) form.setValue("brandId", brand_id);
  }, [brand_id, form.setValue]);

  useEffect(() => {
    setIsDirty(form.formState.isDirty);
  }, [form.formState.isDirty, setIsDirty]);

  useEffect(() => {
    const unsubscribe = useIsRequestFormDirty.subscribe(
      (state) => state.isDirty,
      (isDirty) => {
        if (!isDirty) {
          form.reset(form.getValues());
        }
      },
    );

    return () => unsubscribe();
  }, [form]);

  return {
    ...form,
    setShowWarningModal,
    showWarningModal,
  };
}

export function useCreateWebsiteImplementationRequestFormContext() {
  return useFormContext<CreateWebsiteImplementationRequestFormData>();
}
